import { baseApiEndpoint } from 'config';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken } from 'features/auth/helpers/helpers';
import {
  CurrentUserErrorStatusesHandledByInterceptor,
  ImportFileExtensions,
  ImportTemplateFileName,
} from 'features/user/constants';
import {
  type TImportUserExcelResponse,
  type TImportUserExcelRow,
  type TMeResponse,
  type TUser,
  type TUserInfo,
} from 'features/user/types';

/** Display name of an account: lowercase name first, then uppercase, then username. */
export const getUserDisplayName = (
  user: Pick<TUser, 'name_lowercase' | 'name_uppercase' | 'username'>
) => user.name_lowercase || user.name_uppercase || user.username;

/**
 * Map the `GET /user/me` payload onto `TUserInfo`; fields it does not return stay absent.
 * `gender` is left out on purpose: the identity service stores it as free text, not as the app's `GENDER` enum.
 */
export const mapMeToCurrentUser = (me: TMeResponse): TUserInfo => ({
  code: String(me.id),
  username: me.email,
  email: me.email,
  name_uppercase: me.displayName || me.name || me.email,
  phone: me.phoneNumber ?? '',
  home_phone: me.personalPhoneNumber ?? '',
  birthday: me.dob,
  department: { code: me.departmentCode, name: me.departmentName ?? '' },
  // The identity service has no company entity: the partner code stands in for both code and name.
  company: { code: me.partnerCode, name: me.partnerCode },
  // The identity service has no position either: the account's roles are shown as its position.
  position: {
    code: me.roles[0] ? String(me.roles[0].id) : '',
    name: me.roles.map(role => role.name).join(', '),
  },
  partner_code: me.partnerCode,
  roles: me.roles,
  permissions: me.permissions,
});

/**
 * True when a failed `GET /user/me` must end the session (timeout / 408, 404, 5xx, network or malformed
 * response). `/me` is the root request of every private page, so its failure must never fall through to an
 * empty profile. 401 and 403 are left to the axios interceptor (refresh token / forbidden page).
 *
 * @param error - The `error` of the `useGetCurrentUserQuery` result (a `TQueryError` or a serialized error).
 */
export const shouldEndSessionOnCurrentUserError = (error: unknown) => {
  const status = (error as { status?: unknown } | undefined)?.status;

  return !CurrentUserErrorStatusesHandledByInterceptor.some(
    handledStatus => handledStatus === status
  );
};

/** Rows parsed from the Excel file — `POST /import-excel` answers the array itself or wraps it in `data`. */
export const getImportedUserRows = (
  response?: TImportUserExcelResponse
): TImportUserExcelRow[] =>
  !response ? [] : Array.isArray(response) ? response : (response.data ?? []);

/** True when the file name ends with one of the extensions the import screen accepts. */
export const hasImportFileExtension = (fileName: string) =>
  ImportFileExtensions.some(extension =>
    fileName.toLowerCase().endsWith(extension)
  );

/**
 * Download the Excel import template. It bypasses RTK Query on purpose:
 * `axiosBaseQuery` cannot forward `responseType`, so a Blob never comes back.
 */
export const downloadUserImportTemplate = async () => {
  const response = await fetch(`${baseApiEndpoint}/template-account`, {
    headers: {
      Authorization: `Bearer ${getAuthToken(TOKEN.ACCESS_TOKEN)}`,
    },
  });

  if (!response.ok) throw new Error('Không tải được file mẫu');

  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = ImportTemplateFileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);
};
